import { useMutation } from "@apollo/client/react";
import { CloseOutlined, CreditCardOutlined } from "@ant-design/icons";
import { Alert, App, Button, Descriptions, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnreachableCaseError } from "ts-essentials";
import {
  CreateBillingPortalSessionDocument,
  CreateCheckoutSessionDocument,
  PlanType,
  SubscriptionStatus,
  type TeamFragment,
  TeamRole,
} from "../../gql/generated/graphqlSchema.tsx";
import { useTeamAccessState } from "../../hooks/useTeamAccessState.ts";
import { translateServiceError } from "../../hooks/useServiceError.ts";
import { fitContent, title } from "../../styles/shared.styles.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import { PricingCards } from "../../components/PricingCards.tsx";

type Props = {
  readonly role: TeamRole;
};

export const BillingSection = ({ role }: Props) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { team } = useTeamAccessState();
  const isAdmin = role === TeamRole.Admin;

  // Stripe Checkout's success_url contains ?checkout=success. Show a one-time
  // confirmation banner derived from the initial URL, then strip the param
  // (side effect, no setState) so a refresh doesn't replay it.
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(
    () => new URLSearchParams(window.location.search).get("checkout") === "success",
  );
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("checkout")) return;
    params.delete("checkout");
    const search = params.toString();
    const url = window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
    window.history.replaceState({}, "", url);
  }, []);

  const showError = (error: unknown) => {
    const msg = translateServiceError(error, t);
    if (msg) void message.error(msg);
  };
  const [createCheckout, { loading: checkoutLoading }] = useMutation(CreateCheckoutSessionDocument, {
    onCompleted: (res) => window.location.assign(res.createCheckoutSession),
    onError: showError,
  });
  const [createPortal, { loading: portalLoading }] = useMutation(CreateBillingPortalSessionDocument, {
    onCompleted: (res) => window.location.assign(res.createBillingPortalSession),
    onError: showError,
  });

  if (!team) return <PageLoader />;

  const selectPlan = (plan: PlanType) => void createCheckout({ variables: { plan } });
  const openPortal = () => void createPortal();

  return (
    <Space orientation="vertical" size="middle">
      {showCheckoutSuccess ? (
        <Alert
          closable={{ onClose: () => setShowCheckoutSuccess(false), closeIcon: <CloseOutlined /> }}
          description={t("billing.checkoutSuccessBody")}
          showIcon
          title={t("billing.checkoutSuccessTitle")}
          type="success"
        />
      ) : null}

      {isAdmin ? (
        <>
          {/* Show the Subscribe section unless an active (or past-due) sub already covers the team. */}
          {team.subscriptionStatus !== SubscriptionStatus.Active &&
            team.subscriptionStatus !== SubscriptionStatus.PastDue && (
              <>
                <Typography.Title className={title} level={4}>
                  {t("billing.sectionSubscribe")}
                </Typography.Title>
                <PricingCards loading={checkoutLoading} onSelect={selectPlan} />
              </>
            )}
          {/* Show the Manage section anytime the team has ever subscribed — the Stripe Customer
              still holds invoices, payment methods, and tax info worth surfacing even after
              cancellation. subscriptionStatus is null only when the team never reached Checkout. */}
          {team.subscriptionStatus != null && (
            <>
              <Typography.Title className={title} level={4}>
                {t("billing.sectionManage")}
              </Typography.Title>
              <Button icon={<CreditCardOutlined />} loading={portalLoading} onClick={openPortal} type="primary">
                {t("billing.manageBilling")}
              </Button>
              <Typography.Text type="secondary">{t("billing.stripeNote")}</Typography.Text>
            </>
          )}
        </>
      ) : (
        <>
          <Typography.Title className={title} level={4}>
            {t("billing.sectionManage")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("billing.adminOnly")}</Typography.Text>
        </>
      )}

      <Typography.Title className={title} level={4}>
        {t("billing.sectionStatus")}
      </Typography.Title>
      <Descriptions bordered className={fitContent} column={1}>
        {team.subscriptionStatus == null && (
          <Descriptions.Item label={t("billing.freeAccessUntil")}>
            {formatDateTime(team.freeAccessUntil, i18n.language)}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("billing.subscriptionStatus")}>
          <SubscriptionStatusLabel team={team} />
        </Descriptions.Item>
      </Descriptions>
    </Space>
  );
};

// Folds the relevant date into the status text so we don't need a separate
// "period" row. Prioritization: a scheduled cancellation ("Active until X")
// overrides the normal renewal date; a completed cancellation reads "Canceled on X".
const SubscriptionStatusLabel = ({ team }: { readonly team: TeamFragment }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const status = team.subscriptionStatus;
  if (status == null) return t("billing.statusNone");
  switch (status) {
    case SubscriptionStatus.Active:
      if (team.cancelAt != null) {
        return `${t("billing.subscriptionWillEndOn")} ${formatDateTime(team.cancelAt, locale)}`;
      }
      if (team.currentPeriodEnd != null) {
        return `${t("billing.statusActive")} · ${t("billing.currentPeriodEnd")}: ${formatDateTime(team.currentPeriodEnd, locale)}`;
      }
      return t("billing.statusActive");
    case SubscriptionStatus.Canceled:
      if (team.endedAt != null) {
        return `${t("billing.subscriptionEndedOn")} ${formatDateTime(team.endedAt, locale)}`;
      }
      return t("billing.statusCanceled");
    case SubscriptionStatus.Trialing:
      if (team.currentPeriodEnd != null) {
        return `${t("billing.statusTrialing")} · ${t("billing.currentPeriodEnd")}: ${formatDateTime(team.currentPeriodEnd, locale)}`;
      }
      return t("billing.statusTrialing");
    case SubscriptionStatus.PastDue:
      return t("billing.statusPastDue");
    case SubscriptionStatus.Unpaid:
      return t("billing.statusUnpaid");
    case SubscriptionStatus.Paused:
      return t("billing.statusPaused");
    case SubscriptionStatus.Incomplete:
      return t("billing.statusIncomplete");
    case SubscriptionStatus.IncompleteExpired:
      return t("billing.statusIncompleteExpired");
    default:
      throw new UnreachableCaseError(status);
  }
};
